import { GridStack } from "gridstack";
import "gridstack/dist/gridstack.min.css";
import { Fancybox } from "@fancyapps/ui/src/Fancybox";
import ApplicationController from "./application_controller";
import { loadGoogleCharts, drawChartFromData } from "../utils/charts";

export default class extends ApplicationController {
  static targets = [
    "grid",
    "chip",
    "current_app",
    "widgetCategory",
    "searchInput",
    "noResultMessage",
  ];

  static values = {
    url: String,
    dashId: Number,
    currentApp: String,
    popupMessageUrl: String,
  };

  connect() {
    this.changeColor();

    // ⚠️ évite de re-brancher 10 fois les handlers si Turbo/Stimulus reconnect
    this._isConnected = true;

    this.selectedChips = new Set();
    this.chipTargets.forEach((chip) => {
      if (chip.classList.contains("selected")) {
        this.selectedChips.add(chip.dataset.id);
      }
    });

    if (this.hasPopupMessageUrlValue) {
      this.getPopupMessage();
    }

    // --- init grid ---
    if (this.hasGridTarget) this.initGridStack();

    // ❌ CAUSE DU BUG : compact au connect => ça remonte tout à chaque refresh
    // if (this.hasGridTarget && this.grid) this.compactAllWidgetsUp();

    // --- ton mode "dashloader" (je le laisse, mais sans re-init grid inutilement) ---
    if (!this.hasUrlValue || !this.hasDashIdValue) {
      // console.warn("⚠️ dashloader: url or dashId is missing, skipping AJAX.");
    } else {
      const url = this.urlValue;

      fetch(url)
        .then((response) => {
          if (!response.ok) throw new Error(`HTTP ${response.status}`);
          return response.text();
        })
        .then((html) => {
          this.element.innerHTML = html;

          // Recharge Stimulus si besoin
          this.reconnectStimulusControllers(this.element);

          // Re-init GridStack seulement si nécessaire
          if (this.hasGridTarget) this.initGridStack();

          const eventName = `dash:load_${this.dashIdValue}`;
          document.dispatchEvent(new CustomEvent(eventName));
        })
        .catch((error) => {
          this.element.innerHTML =
            "<div class='alert alert-danger'>Erreur lors du chargement du widget.</div>";
          console.error("❌ Erreur AJAX dashloader :", error);
        });
    }

    // --- listeners globaux : bind + store pour pouvoir remove() dans disconnect ---
    this._onReflexDone = this.handleReflexDone.bind(this);
    document.addEventListener("dashboard:reflex-done", this._onReflexDone);

    this._onWidgetContentUpdated = this.onWidgetContentUpdated.bind(this);
    document.addEventListener(
      "widget:content-updated",
      this._onWidgetContentUpdated
    );
  }
  disconnect() {
    if (this._onReflexDone) {
      document.removeEventListener("dashboard:reflex-done", this._onReflexDone);
      this._onReflexDone = null;
    }
    if (this._onWidgetContentUpdated) {
      document.removeEventListener(
        "widget:content-updated",
        this._onWidgetContentUpdated
      );
      this._onWidgetContentUpdated = null;
    }

    // ✅ si la grille existe toujours dans le DOM, on NE la détruit PAS
    const gridStillInDom =
      this.grid?.el && document.body.contains(this.grid.el);
    if (gridStillInDom) return;

    // ✅ sinon, on peut cleanup
    if (this.grid) {
      if (window.myDashboard?.grid === this.grid)
        window.myDashboard.grid = null;
      try {
        this.grid.destroy(false);
      } catch (e) {}
      this.grid = null;
    }
  }

  initGridStack() {
    if (!this.hasGridTarget) {
      console.info("Il n'y a pas de gridStack dans le DOM");
      return;
    }

    // ✅ GUARD : si déjà init, ne pas ré-init (ça multiplie les listeners et les re-layout)
    if (this.grid && this.grid.el === this.gridTarget) return;

    // si une ancienne instance existe (ex: reconnect), on destroy proprement
    if (this.grid) {
      try {
        this.grid.destroy(false);
      } catch (e) {}
      this.grid = null;
    }

    this.grid = GridStack.init(
      {
        handle: ".drag-handle",
        cellHeight: 5,
        animate: true,
        float: true,
        resizable: { handles: "all" },
      },
      this.gridTarget
    );

    window.myDashboard = {
      grid: this.grid,
      currentPage: parseInt(
        new URLSearchParams(window.location.search).get("page") || 1
      ),
      dashId: this.dashIdValue,
    };

    // 🔒 Flag pour éviter boucle : update() programmatique => "change" => save => etc.
    this._isInternalGridUpdate = false;

    this.grid.on("change", (event, items) => {
      // console.log(
      //   "CHANGE OK",
      //   items.map((i) => i.id)
      // );
      // console.log("change called, internal =", this._isInternalGridUpdate);

      // =====================================================
      // ✅ REDRAW CHARTS UNIQUEMENT
      // =====================================================
      items.forEach((item) => {
        const graphContainer = item.el.querySelector("[data-chart-data]");
        if (!graphContainer) return; // ⛔️ NON-CHART → on sort immédiatement

        const colCount = this.grid.opts.column;
        const gridWidth = this.grid.el.clientWidth;
        const cellWidth = gridWidth / colCount;

        const contentWrapper = item.el.querySelector(
          ".content.table-scroll-wrapper"
        );

        const gridStackItemContentHeight = this.getFullHeight(
          item.el.querySelector(".grid-stack-item-content")
        );

        const extendTitleHeight = this.getFullHeight(
          item.el.querySelector(".extend_title")
        );

        const barreTitreHeight = this.getFullHeight(
          item.el.querySelector(".barre-titre")
        );

        const moreDetailsBlockHeight = item.el.querySelector(
          "[data-chart-more-details]"
        )
          ? 44
          : 0;

        const chartToolsHeight = item.el.querySelector(
          "[data-export-csv-label]"
        )
          ? 26
          : 0;

        const contentWrapperGap =
          chartToolsHeight +
          moreDetailsBlockHeight +
          barreTitreHeight +
          extendTitleHeight;

        const finalHeight =
          gridStackItemContentHeight - contentWrapperGap - 26 - 36;

        // 🔥 IMPORTANT : wrapper réglé AVANT le draw
        if (contentWrapper) {
          contentWrapper.style.height = `${
            gridStackItemContentHeight -
            barreTitreHeight -
            extendTitleHeight -
            36
          }px`;
        }

        loadGoogleCharts(() =>
          drawChartFromData(graphContainer, {
            chartWidth: item.w * cellWidth - 60,
            chartHeight: finalHeight,
            dashId: this.dashIdValue,
          })
        );
      });

      // =====================================================
      // ⛔ BLOQUER UNIQUEMENT LA SAUVEGARDE
      // =====================================================
      if (this._isInternalGridUpdate) return;

      const payload = items.map((item) => ({
        id: item.el.dataset.id,
        grid_config: {
          x: item.x,
          y: item.y,
          w: item.w,
          h: item.h,
          auto_position: item.autoPosition || false,
          no_resize: item.noResize || false,
          no_move: item.noMove || false,
        },
      }));

      fetch(`/${this.currentAppValue}/fdashboards/update_all_settings`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector("meta[name=csrf-token]")
            .content,
        },
        body: JSON.stringify({ settings: payload }),
      });
    });

    this.grid.on("dragstart", (_, el) => {
      el.classList.add("dragging-simulated");
      document.querySelectorAll(".grid-stack-item").forEach((item) => {
        if (item !== el) item.classList.add("highlight-background");
      });
    });

    this.grid.on("dragstop", (_, el) => {
      el.classList.remove("dragging-simulated");
      el.dataset.userMoved = "true";
      document
        .querySelectorAll(".grid-stack-item")
        .forEach((item) => item.classList.remove("highlight-background"));
    });

    this.grid.on("resizestart", (_, el) => {
      el.classList.add("resizing-simulated");
      el.style.removeProperty("height");
      document.querySelectorAll(".grid-stack-item").forEach((item) => {
        if (item !== el) item.classList.add("highlight-background");
      });
    });

    this.grid.on("resizestop", (_, el) => {
      el.classList.remove("resizing-simulated");
      el.style.removeProperty("height");
      el.dataset.userResized = "true";
      document
        .querySelectorAll(".grid-stack-item")
        .forEach((item) => item.classList.remove("highlight-background"));
    });

    this.toggleGridStackItems();
  }

  // ==========================================================
  // handler pour event venant du widgetloader
  // => Resize "safe" SANS y+1/y-1, SANS compact global
  // ==========================================================
  onWidgetContentUpdated(arg) {
    if (!this.grid) return;

    //1) Déterminer settingId / itemEl selon le type d'argument
    let settingId = null;
    let itemEl = null;

    // cas: arg est un élément .grid-stack-item
    if (arg?.classList?.contains("grid-stack-item")) {
      itemEl = arg;
      settingId = itemEl.dataset.id || null;
    }
    // cas: arg est un ID (number/string)
    else if (typeof arg === "string" || typeof arg === "number") {
      settingId = String(arg);
    }
    // cas: arg est un event (CustomEvent)
    else if (arg?.detail?.settingId) {
      settingId = String(arg.detail.settingId);
    }

    if (!itemEl && settingId) {
      itemEl = this.grid.el.querySelector(
        `.grid-stack-item[data-id='${settingId}']`
      );
    }
    // console.log("🔔 widget:content-updated →", { settingId, itemEl });
    if (!itemEl) return;

    // 1) CHART: on laisse le redraw au pipeline "change"
    if (itemEl.querySelector("[data-chart-data]")) {
      this.miniResizeFull(itemEl);
      return;
    }

    // 2) NON-CHART: seulement si auto-resize ON
    const autoIcon = itemEl.querySelector("i[data-auto-resize]");
    const autoVal = autoIcon
      ? parseInt(autoIcon.dataset.autoResize || "2", 10)
      : 2;
    if (autoVal !== 1) return;

    // éviter de relancer 10 fois pendant un filtre qui déclenche plusieurs updates
    if (itemEl.dataset._autoResizeBusy === "1") return;
    itemEl.dataset._autoResizeBusy = "1";

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        try {
          // A) resize full
          this.resizeToContentFull({ currentTarget: itemEl });

          // B) compact en dessous (uniquement pour ce widget)
          const node = this.grid.engine.nodes.find((n) => n.el === itemEl);
          if (node) {
            // anti-boucle compact
            if (!this._isCompacting) {
              this._isCompacting = true;
              try {
                const { targets, changed } = this.compactYOnlyWidgetsBelow(
                  this.grid,
                  node
                );

                this.saveNodesSettings([node, ...targets], {
                  reason: "auto-resize+compact",
                });
              } finally {
                this._isCompacting = false;
              }
            }
          } 
        } finally {
          // libère le lock
          itemEl.dataset._autoResizeBusy = "0";
        }
      });
    });
  }
  // ✅ Sauvegarde toute la grille (snapshot) via update_all_settings
  saveNodesSettings(nodes, { reason = "moved-nodes" } = {}) {
    const grid = this.grid || window.myDashboard?.grid;
    if (!grid?.engine?.nodes) return;

    const uniq = new Map();

    (nodes || []).forEach((n) => {
      // accepte node GridStack OU element
      const node = n?.el ? n : grid.engine.nodes.find((x) => x.el === n);

      if (!node?.el?.dataset?.id) return;

      const id = node.el.dataset.id;
      uniq.set(id, {
        id,
        grid_config: {
          x: node.x,
          y: node.y,
          w: node.w,
          h: node.h,
          auto_position: node.autoPosition || false,
          no_resize: node.noResize || false,
          no_move: node.noMove || false,
        },
      });
    });

    const settings = [...uniq.values()];
    if (!settings.length) return;

    // console.log("💾 saveNodesSettings", { reason, count: settings.length });

    fetch(`/${this.currentAppValue}/fdashboards/update_all_settings`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": document.querySelector("meta[name=csrf-token]")
          ?.content,
      },
      body: JSON.stringify({ settings }),
    })
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
      })
      .catch((err) => console.error("❌ saveNodesSettings error:", err));
  }

  miniResizeFull(arg) {
    const itemEl = arg?.classList?.contains("grid-stack-item")
      ? arg
      : arg?.currentTarget?.closest?.(".grid-stack-item") ||
        arg?.target?.closest?.(".grid-stack-item");

    const grid = this.grid || window.myDashboard?.grid;
    if (!itemEl || !grid) return;

    const node = grid.engine.nodes.find((n) => n.el === itemEl);
    if (!node) return;

    this._isInternalGridUpdate = true;
    try {
      grid.batchUpdate();

      // micro resize réel (déclenche change)
      grid.update(itemEl, { w: node.w, h: node.h + 1 });
      grid.update(itemEl, { w: node.w, h: node.h });

      grid.commit();
    } finally {
      this._isInternalGridUpdate = false;
    }
  }

  resizeItemToContentSafe(itemEl) {
    const grid = window.myDashboard?.grid;
    if (!grid || !itemEl) return;

    const widgetId = itemEl.dataset.id;
    const container = itemEl.querySelector(".dash_container");
    const contentWrapper = itemEl.querySelector(
      ".content.table-scroll-wrapper"
    );

    if (!container || !widgetId) return;

    const contentSize = this.getContentSize(contentWrapper);
    const extendTitleHeight = this.getFullHeight(
      itemEl.querySelector(".extend_title")
    );
    const barreTitreHeight = this.getFullHeight(
      itemEl.querySelector(".barre-titre")
    );

    const cellHeight = grid.getCellHeight(true);
    const colCount = grid.opts.column;
    const gridWidth = grid.el.clientWidth;
    const cellWidth = gridWidth / colCount;

    const containerHeight =
      barreTitreHeight + extendTitleHeight + (contentSize?.height || 0) + 36 ||
      container.offsetHeight;
    const contentWidth = contentSize?.width || container.offsetWidth;

    const newHeight = Math.max(1, Math.ceil(containerHeight / cellHeight));
    let newWidth = Math.min(
      colCount,
      Math.max(1, Math.ceil(contentWidth / cellWidth))
    );
    if (contentWidth > newWidth * cellWidth && newWidth < colCount)
      newWidth += 1;

    const node = grid.engine.nodes.find((n) => n.el === itemEl);
    if (!node) return;

    // ✅ update SANS toucher y (donc pas de glissement)
    this._isInternalGridUpdate = true;
    try {
      grid.batchUpdate();
      grid.update(itemEl, {
        width: newWidth,
        height: newHeight,
        x: node.x,
        y: node.y,
      });
      grid.commit();
    } finally {
      this._isInternalGridUpdate = false;
    }

    // persist (même logique que chez toi)
    const finalNode = grid.engine.nodes.find((n) => n.el === itemEl) || node;
    const config = {
      x: finalNode.x,
      y: finalNode.y,
      w: finalNode.w,
      h: finalNode.h,
      auto_position: finalNode.autoPosition || false,
      no_resize: finalNode.noResize || false,
      no_move: finalNode.noMove || false,
    };

    fetch(`/${this.currentAppValue}/fdashboards/${widgetId}/update_setting`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": document.querySelector("meta[name='csrf-token']")
          .content,
      },
      body: JSON.stringify({ grid_config: config }),
    }).catch(() => {});
  }

  // -------------------------
  // Le reste : inchangé (je garde tout ton code)
  // -------------------------

  searchWidgets(event) {
    const normalize = (str) =>
      str
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .toLowerCase();

    const query = normalize(event.target.value);
    const chips = this.element.querySelectorAll(".chip");
    const categories = this.element.querySelectorAll(".widget-category");
    let totalVisibleChips = 0;

    chips.forEach((chip) => {
      const text = normalize(chip.textContent);
      const isVisible = text.includes(query);
      chip.style.display = isVisible ? "" : "none";
      if (isVisible) totalVisibleChips++;
    });

    categories.forEach((category) => {
      const visibleChips = category.querySelectorAll(
        '.chip:not([style*="display: none"])'
      );
      category.style.display = visibleChips.length > 0 ? "" : "none";
    });

    if (this.hasNoResultMessageTarget) {
      this.noResultMessageTarget.style.display =
        totalVisibleChips === 0 ? "flex" : "none";
    }
  }

  toggleGridStackItems() {
    document
      .querySelectorAll(".grid-stack-item")
      .forEach((e) => e.classList.remove("fx-d-none"));
    const skeleton = document.getElementById("fx-widgets-skeleton");
    skeleton?.classList.add("fx-d-none");
    skeleton?.classList.remove("fx-d-flex");
  }

  changePage(event) {
    event.preventDefault();
    event.stopImmediatePropagation();
    const page = event.currentTarget.dataset.page;
    this.stimulate("FdashboardReflex#change_page", page);
  }

  handleReflexDone(event) {
    const { id, page, widgetid, reflex } = event.detail;

    if (reflex === "dash_setting#destroy") {
      const el = document.querySelector(`.grid-stack-item[data-id='${id}']`);
      if (el && this.grid) {
        var fx_wid_count_page = document.getElementById(
          `fx_wid_count_page_${page}`
        );
        var fx_wid_chip = document.getElementById(`fx_wid_chip_${widgetid}`);
        var fx_wid_chip_page_number = document.getElementById(
          `fx_wid_chip_page_number_${widgetid}`
        );
        var fx_wid_chip_add_to_page = document.getElementById(
          `fx_wid_chip_add_to_page_${widgetid}`
        );
        var fx_wid_chip_move_to_page = document.getElementById(
          `fx_wid_chip_move_to_page_${widgetid}`
        );

        if (fx_wid_count_page) {
          let count = parseInt(fx_wid_count_page.textContent, 10);
          if (!isNaN(count) && count > 1) {
            fx_wid_count_page.textContent = count - 1;
          } else {
            fx_wid_count_page.classList.add("fx-d-none");
          }
        }

        if (fx_wid_chip) fx_wid_chip.classList.remove("selected");
        if (fx_wid_chip_add_to_page)
          fx_wid_chip_add_to_page.classList.remove("fx-d-none");
        if (fx_wid_chip_page_number)
          fx_wid_chip_page_number.classList.add("fx-d-none");
        if (fx_wid_chip_move_to_page)
          fx_wid_chip_move_to_page.classList.add("fx-d-none");

        this.grid.removeWidget(el);
        // ne plus compacter après suppression
      }
    }
  }

  moveToPage(event) {
    event.preventDefault();
    event.stopImmediatePropagation();

    const widgetId = event.currentTarget.dataset.id;
    const targetPage = event.currentTarget.dataset.targetPage;
    const settingId = event.currentTarget.dataset.settingId;
    const current_app = event.currentTarget.dataset.currentApp;
    const currentPage = event.currentTarget.dataset.currentPage;
    const sourcePage = event.currentTarget.dataset.sourcePage;
    const graphType = event.currentTarget.dataset.graphType;

    const fx_wid_move_to_page_btn = document.getElementById(
      `fx_wid_move_to_page_btn_${targetPage}`
    );
    const fx_wid_move_to_page_btn_loader = document.getElementById(
      `fx_wid_move_to_page_btn_loader_${targetPage}`
    );
    const fx_wid_chip_page_number = document.getElementById(
      `fx_wid_chip_page_number_${widgetId}`
    );

    // je recipere le chip dès le départ, avant que le DOM ne bouge
    const chip = event.currentTarget.closest(".chip");

    if (fx_wid_move_to_page_btn)
      fx_wid_move_to_page_btn.classList.add("fx-d-none");
    if (fx_wid_move_to_page_btn_loader)
      fx_wid_move_to_page_btn_loader.classList.remove("fx-d-none");

    fetch(`/${current_app}/fdashboards/move_widget`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": document.querySelector("meta[name='csrf-token']")
          .content,
      },
      body: JSON.stringify({
        dash_library_id: widgetId,
        new_page: targetPage,
        setting_id: settingId,
      }),
    })
      .then((res) => {
        if (fx_wid_move_to_page_btn)
          fx_wid_move_to_page_btn.classList.remove("fx-d-none");
        if (fx_wid_move_to_page_btn_loader)
          fx_wid_move_to_page_btn_loader.classList.add("fx-d-none");

        if (!res.ok) {
          console.error("❌ move_widget: requête échouée");
          return;
        }
        return res.json();
      })
      .then((data) => {
        if (!data || !chip) return;

        if (fx_wid_chip_page_number) {
          fx_wid_chip_page_number.textContent = "page " + targetPage;
        }

        const el = document.querySelector(
          `.grid-stack-item[data-id='${settingId}']`
        );
        if (el && window.myDashboard?.grid) {
          window.myDashboard.grid.removeWidget(el);
        }

        const sourceCount = document.getElementById(
          `fx_wid_count_page_${sourcePage}`
        );
        const targetCount = document.getElementById(
          `fx_wid_count_page_${targetPage}`
        );

        if (sourceCount) {
          let count = parseInt(sourceCount.textContent, 10);
          if (!isNaN(count) && count > 1) {
            sourceCount.textContent = count - 1;
          } else {
            sourceCount.classList.add("fx-d-none");
          }
        }

        if (targetCount) {
          let count = parseInt(targetCount.textContent, 10);
          if (!isNaN(count)) {
            targetCount.textContent = count + 1;
            targetCount.classList.remove("fx-d-none");
          }
        }

        // Réorganise dropdown localement dans le chip déjà capturé
        const sourceDropdownItem = chip
          .querySelector(`#fx_wid_move_to_page_btn_${sourcePage}`)
          ?.closest(".dropdown-item");
        const targetDropdownItem = chip
          .querySelector(`#fx_wid_move_to_page_btn_${targetPage}`)
          ?.closest(".dropdown-item");

        if (sourceDropdownItem)
          sourceDropdownItem.classList.remove("fx-d-none");
        if (targetDropdownItem) targetDropdownItem.classList.add("fx-d-none");

        // Met à jour tous les boutons de ce dropdown
        const dropdown = chip.querySelector(".fx-dropdown-content");
        if (dropdown) {
          dropdown
            .querySelectorAll(`[id^='fx_wid_move_to_page_btn_']`)
            .forEach((link) => {
              link.dataset.sourcePage = targetPage;
            });
        }

        if (data.status === "ok" && currentPage === targetPage) {
          this.addToTargetPage(
            widgetId,
            current_app,
            data.setting_id,
            data.grid_config,
            graphType
          );
        }
      });
  }

  addToTargetPage(
    dashLibraryId,
    current_app,
    setting_id,
    grid_config,
    graphType
  ) {
    fetch(
      `/${current_app}/fdashboards/widget_card?id=${dashLibraryId}&graph_type=${graphType}`
    )
      .then((res) => res.text())
      .then((html) => {
        const el = document.createElement("div");
        el.classList.add("grid-stack-item");
        el.dataset.id = setting_id;

        el.setAttribute("gs-x", grid_config.x);
        el.setAttribute("gs-y", grid_config.y);
        el.setAttribute("gs-w", grid_config.w);
        el.setAttribute("gs-h", grid_config.h);
        el.setAttribute("gs-auto-position", grid_config.auto_position);
        el.setAttribute("gs-no-resize", grid_config.no_resize);
        el.setAttribute("gs-no-move", grid_config.no_move);

        el.innerHTML = `
          <div class="grid-stack-item-content card shadow-sm p-2" id="grid-stack-item-content-${dashLibraryId}">
            ${html}
          </div>
        `;

        if (window.myDashboard?.grid) {
          window.myDashboard.grid.el.appendChild(el);
          window.myDashboard.grid.makeWidget(el);
        }

        $("#cross_widget table").addClass(
          "fx-table-hm cross fx-table-hm-wrapped fx-table-12"
        );
        $('#cross_widget input[type="submit"]').addClass("mt-2 fx-button");
      });
  }

  guardDisabled(event) {
    const container = event.currentTarget.closest('[aria-disabled="true"]');
    if (container && container.getAttribute("aria-disabled") === "true") {
      event.preventDefault();
      event.stopImmediatePropagation();
      return false;
    }
  }

  addToPage(event) {
    event.preventDefault();
    event.stopImmediatePropagation();

    const dashLibraryId = event.currentTarget.dataset.id;
    const dash_page = event.currentTarget.dataset.dashPage;

    const num_page = event.currentTarget.dataset.targetPage;
    const current_page = event.currentTarget.dataset.currentPage;
    const current_app = event.currentTarget.dataset.currentApp;
    const graphType = event.currentTarget.dataset.graphType;
    const fx_wid_count_target_page = document.getElementById(
      `fx_wid_count_page_${num_page}`
    );
    const fx_wid_count_current_page = document.getElementById(
      `fx_wid_count_page_${current_page}`
    );
    const fx_wid_chip_page_number = document.getElementById(
      `fx_wid_chip_page_number_${dashLibraryId}`
    );
    const fx_wid_chip_add_to_page = document.getElementById(
      `fx_wid_chip_add_to_page_${dashLibraryId}`
    );
    const fx_wid_chip_move_to_page = document.getElementById(
      `fx_wid_chip_move_to_page_${dashLibraryId}`
    );
    const fx_wid_chip = document.getElementById(`fx_wid_chip_${dashLibraryId}`);

    const fx_wid_add_to_page_btn = document.getElementById(
      `fx_wid_add_to_page_btn_${num_page}`
    );
    const fx_wid_add_to_page_btn_loader = document.getElementById(
      `fx_wid_add_to_page_btn_loader_${num_page}`
    );

    if (fx_wid_add_to_page_btn) {
      fx_wid_add_to_page_btn.classList.add("fx-d-none");
    }

    if (fx_wid_add_to_page_btn_loader) {
      fx_wid_add_to_page_btn_loader.classList.remove("fx-d-none");
    }

    fetch(`/${current_app}/fdashboards/add_widget`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": document.querySelector("[name='csrf-token']").content,
      },
      body: JSON.stringify({
        dash_library_id: dashLibraryId,
        num_page: num_page,
        dash_page: dash_page,
      }),
    })
      .then((res) => res.json())
      .then((data) => {
        const { setting_id, grid_config } = data;
        return fetch(
          `/${current_app}/fdashboards/widget_card?id=${dashLibraryId}&graph_type=${graphType}`
        )
          .then((res) => res.text())
          .then((html) => {
            const el = document.createElement("div");
            el.classList.add("grid-stack-item");
            el.dataset.id = setting_id;

            el.setAttribute("gs-x", grid_config.x);
            el.setAttribute("gs-y", grid_config.y);
            el.setAttribute("gs-w", grid_config.w);
            el.setAttribute("gs-h", grid_config.h);
            el.setAttribute("gs-auto-position", grid_config.auto_position);
            el.setAttribute("gs-no-resize", grid_config.no_resize);
            el.setAttribute("gs-no-move", grid_config.no_move);

            el.innerHTML = `
          <div class="grid-stack-item-content card shadow-sm p-2" id="grid-stack-item-content-${dashLibraryId}">
           
                        ${html} 
          </div>
        `;

            // console.log("elllll", el);
            // console.log("num_page", num_page);
            // console.log("num_page", num_page);
            if (fx_wid_add_to_page_btn) {
              fx_wid_add_to_page_btn.classList.remove("fx-d-none");
            }

            if (fx_wid_add_to_page_btn_loader) {
              fx_wid_add_to_page_btn_loader.classList.add("fx-d-none");
            }

            if (num_page == current_page) {
              if (window.myDashboard?.grid)
                window.myDashboard.grid.makeWidget(el);
            } else {
            }

            if (fx_wid_count_target_page) {
              let count = parseInt(fx_wid_count_target_page.textContent, 10);
              if (!isNaN(count)) {
                fx_wid_count_target_page.textContent = count + 1;
                fx_wid_count_target_page.classList.remove("fx-d-none");
              }
            }

            if (fx_wid_chip) {
              fx_wid_chip.classList.add("selected");
            }

            if (fx_wid_chip_add_to_page) {
              fx_wid_chip_add_to_page.classList.add("fx-d-none");
            }

            if (fx_wid_chip_page_number) {
              fx_wid_chip_page_number.classList.remove("fx-d-none");
              fx_wid_chip_page_number.textContent = "page " + num_page;
            }

            if (fx_wid_chip_move_to_page) {
              fx_wid_chip_move_to_page.classList.remove("fx-d-none");

              // Mise à jour de tous les <a> dans le dropdown
              const dropdownLinks = fx_wid_chip_move_to_page.querySelectorAll(
                "a[data-target-page]"
              );
              dropdownLinks.forEach((link) => {
                link.dataset.sourcePage = num_page;
                link.dataset.settingId = setting_id;
              });
              // Cacher le bouton move_to_page "Déplacer vers la page (page_actuelle)" dans le dropdown
              const sourceDropdownItem = fx_wid_chip_move_to_page
                .querySelector(`#fx_wid_move_to_page_btn_${num_page}`)
                ?.closest(".dropdown-item");
              if (sourceDropdownItem)
                sourceDropdownItem.classList.add("fx-d-none");
            }

            $("#cross_widget table").addClass(
              "fx-table-hm cross fx-table-hm-wrapped fx-table-12"
            );
            $('#cross_widget input[type="submit"]').addClass("mt-2 fx-button");
          });
      });
  }
  compact() {
    if (this.grid) {
      this.compactYOnly(this.grid);
    }
  }

  openWidgetSelection(event) {
    event.preventDefault();
    event.stopImmediatePropagation();

    if (typeof Fancybox !== "undefined") {
      Fancybox.show([{ src: "#widget-selection", type: "inline" }]);
    } else {
      console.warn("⚠️ Fancybox non chargé !");
    }
  }

  setFixedHeight(arg, fixedPx = 250) {
    // ✅ Par défaut: arg = event. Si arg est itemEl -> on ignore event.
    const itemEl = arg?.classList?.contains("grid-stack-item")
      ? arg
      : arg?.currentTarget?.closest?.(".grid-stack-item") ||
        arg?.target?.closest?.(".grid-stack-item");

    const grid = this.grid || window.myDashboard?.grid;
    if (!itemEl || !grid) {
      console.warn("⛔️ Pas d’élément grid-stack ou GridStack non dispo.");
      return;
    }

    // Charts : anim + exit
    if (itemEl.querySelector("[data-chart-data]")) {
      itemEl.classList.add("resizing-simulated");
      setTimeout(() => itemEl.classList.remove("resizing-simulated"), 300);
      itemEl.style.removeProperty("height");
      return;
    }

    const widgetId = itemEl.dataset.id;
    const container = itemEl.querySelector(".dash_container");
    const contentWrapper = itemEl.querySelector(
      ".content.table-scroll-wrapper"
    );
    if (!container || !widgetId) {
      console.warn("⛔️ Container ou widgetId manquant.");
      return;
    }

    const contentSize = this.getContentSize(contentWrapper);
    const extendTitleHeight = this.getFullHeight(
      itemEl.querySelector(".extend_title")
    );
    const barreTitreHeight = this.getFullHeight(
      itemEl.querySelector(".barre-titre")
    );

    const cellHeight = grid.getCellHeight(true);
    const colCount = grid.opts.column;
    const gridWidth = grid.el.clientWidth;
    const cellWidth = gridWidth / colCount;

    // hauteur “réelle” du contenu, puis on force au max fixedPx
    const containerHeight =
      barreTitreHeight + extendTitleHeight + (contentSize?.height || 0) + 36 ||
      container.offsetHeight;

    const contentWidth = contentSize?.width || container.offsetWidth;

    const forcedContainerHeight = Math.min(containerHeight, fixedPx);
    const newH = Math.max(1, Math.ceil(forcedContainerHeight / cellHeight));

    // largeur recalculée
    let newW = Math.min(
      colCount,
      Math.max(1, Math.ceil(contentWidth / cellWidth))
    );
    if (contentWidth > newW * cellWidth && newW < colCount) newW += 1;

    const node = grid.engine.nodes.find((n) => n.el === itemEl);
    if (!node) {
      console.warn("⛔️ Node GridStack introuvable.");
      return;
    }

    // Rien à faire si déjà identique
    if (node.w === newW && node.h === newH) return;

    this._isInternalGridUpdate = true;
    try {
      grid.batchUpdate();

      itemEl.setAttribute("gs-auto-position", "false");

     
      grid.update(itemEl, {
        x: node.x,
        y: node.y,
        w: newW,
        h: newH,
      });

      grid.commit();
    } finally {
      this._isInternalGridUpdate = false;
    }

    // Persist (prend le node final)

    const finalNode = grid.engine.nodes.find((n) => n.el === itemEl) || node;
    const config = {
      x: finalNode.x,
      y: finalNode.y,
      w: finalNode.w,
      h: finalNode.h,
      auto_position: finalNode.autoPosition || false,
      no_resize: finalNode.noResize || false,
      no_move: finalNode.noMove || false,
    };

    fetch(`/${this.currentAppValue}/fdashboards/${widgetId}/update_setting`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": document.querySelector("meta[name='csrf-token']")
          .content,
      },
      body: JSON.stringify({ grid_config: config }),
    }).catch((err) => console.error("❌ Erreur fetch:", err));

    // Anim
    itemEl.classList.add("resizing-simulated");
    setTimeout(() => itemEl.classList.remove("resizing-simulated"), 300);
    itemEl.style.removeProperty("height");
    const nsode = this.grid.engine.nodes.find((n) => n.el === itemEl);
    if (nsode) {
      // anti-boucle compact
      if (!this._isCompacting) {
        this._isCompacting = true;
        try {
          const { targets, changed } = this.compactYOnlyWidgetsBelow(
            this.grid,
            nsode
          );

          this.saveNodesSettings([nsode, ...targets], {
            reason: "auto-resize+compact",
          });
        } finally {
          this._isCompacting = false;
        }
      }
    }
  }

  resizeToContentFull(arg) {
    // arg = event (DOM) par défaut. Si arg est un itemEl -> on ignore event.
    const itemEl = arg?.classList?.contains("grid-stack-item")
      ? arg
      : arg?.currentTarget?.closest?.(".grid-stack-item") ||
        arg?.target?.closest?.(".grid-stack-item");

    const grid = this.grid || window.myDashboard?.grid;
    if (!itemEl || !grid) return;

    // charts : pas de resize ici
    if (itemEl.querySelector("[data-chart-data]")) {
      itemEl.classList.add("resizing-simulated");
      setTimeout(() => itemEl.classList.remove("resizing-simulated"), 300);
      itemEl.style.removeProperty("height");
      return;
    }

    const widgetId = itemEl.dataset.id;
    const container = itemEl.querySelector(".dash_container");
    const contentWrapper = itemEl.querySelector(
      ".content.table-scroll-wrapper"
    );
    if (!container || !widgetId) return;

    const getFullHeight = (el) => {
      if (!el) return 0;
      const cs = window.getComputedStyle(el);
      return (
        el.offsetHeight +
        parseFloat(cs.marginTop || 0) +
        parseFloat(cs.marginBottom || 0)
      );
    };
    const getContentSize = (el) => {
      if (!el)
        return { width: container.offsetWidth, height: container.offsetHeight };
      return { width: el.scrollWidth, height: el.scrollHeight };
    };

    const contentSize = getContentSize(contentWrapper);
    const extendTitleHeight = getFullHeight(
      itemEl.querySelector(".extend_title")
    );
    const barreTitreHeight = getFullHeight(
      itemEl.querySelector(".barre-titre")
    );

    const cellHeight = grid.getCellHeight(true);
    const colCount = grid.opts.column;
    const gridWidth = grid.el.clientWidth;
    const cellWidth = gridWidth / colCount;

    const containerHeight =
      barreTitreHeight + extendTitleHeight + (contentSize?.height || 0) + 36 ||
      container.offsetHeight;
    const contentWidth = contentSize?.width || container.offsetWidth;

    const newH = Math.max(1, Math.ceil(containerHeight / cellHeight));
    let newW = Math.min(
      colCount,
      Math.max(1, Math.ceil(contentWidth / cellWidth))
    );
    if (contentWidth > newW * cellWidth && newW < colCount) newW += 1;

    const node = grid.engine.nodes.find((n) => n.el === itemEl);
    if (!node) return;

    // rien à faire
    if (node.w === newW && node.h === newH) return;

    this._isInternalGridUpdate = true;
    try {
      grid.batchUpdate();

      itemEl.setAttribute("gs-auto-position", "false");

      // ✅ IMPORTANT : GridStack = w/h (pas width/height)
      grid.update(itemEl, {
        x: node.x,
        y: node.y,
        w: newW,
        h: newH,
      });

      grid.commit();
    } finally {
      this._isInternalGridUpdate = false;
    }

    // persist
    const finalNode = grid.engine.nodes.find((n) => n.el === itemEl) || node;
    const config = {
      x: finalNode.x,
      y: finalNode.y,
      w: finalNode.w,
      h: finalNode.h,
      auto_position: finalNode.autoPosition || false,
      no_resize: finalNode.noResize || false,
      no_move: finalNode.noMove || false,
    };

    fetch(`/${this.currentAppValue}/fdashboards/${widgetId}/update_setting`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": document.querySelector("meta[name='csrf-token']")
          .content,
      },
      body: JSON.stringify({ grid_config: config }),
    }).catch(() => {});

    itemEl.classList.add("resizing-simulated");
    setTimeout(() => itemEl.classList.remove("resizing-simulated"), 300);
    itemEl.style.removeProperty("height");

  
 
    if (itemEl.dataset._autoResizeBusy === "1") return;
    itemEl.dataset._autoResizeBusy = "1";

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        try {
          // B) compact en dessous (uniquement pour ce widget)
   
          if (finalNode) {
            // anti-boucle compact
            if (!this._isCompacting) {
              this._isCompacting = true;
              try {
                const { targets, changed } = this.compactYOnlyWidgetsBelow(
                  this.grid,
                  finalNode
                );

                this.saveNodesSettings([finalNode, ...targets], {
                  reason: "auto-resize+compact",
                });
              } finally {
                this._isCompacting = false;
              }
            }
          }
        } finally {
          itemEl.dataset._autoResizeBusy = "0";
        }
      });
    });
  }

  getElementHeight(parent, selector, defaultHeight = 0) {
    const element = parent.querySelector(selector);
    if (element) return defaultHeight;
    return 0;
  }

  compactYOnly(grid) {
    if (!grid?.engine?.nodes) return;

    // 🧩 Tri par colonne, puis par Y croissant
    const sorted = grid.engine.nodes
      .slice()
      .sort((a, b) => a.x - b.x || a.y - b.y);

    sorted.forEach((node) => {
      let newY = 0;
      // On remonte tant qu’il y a une collision
      while (
        grid.engine.collide(node, { x: node.x, y: newY, w: node.w, h: node.h })
      ) {
        newY++;
      }

      if (newY !== node.y) {
        // console.log(
        //   `⬆️ Widget ${node.el?.dataset?.id} moved y=${node.y} → y=${newY}`
        // );
        grid.engine.moveNode(node, {
          x: node.x,
          y: newY,
          w: node.w,
          h: node.h,
        });
      }
    });

    grid._triggerChangeEvent();
    grid._updateStyles(true);
  }

  compactYOnlyWidgetsBelow(grid, refNode) {
    // console.group("🧲 compactYOnlyWidgetsBelow");

    if (!grid?.engine?.nodes?.length || !refNode) {
      console.warn("⛔️ Grid or refNode not available.");
      // console.groupEnd();
      return { targets: [], changed: [] };
    }

    // --- snapshot BEFORE ---
    const before = new Map();
    grid.engine.nodes.forEach((n) => {
      const id = n?.el?.dataset?.id;
      if (!id) return;
      before.set(id, { x: n.x, y: n.y, w: n.w, h: n.h });
    });

    const nodes = grid.engine.nodes;

    const refX1 = refNode.x;
    const refX2 = refNode.x + refNode.w - 1;
    let refBottom = refNode.y + refNode.h;

    // 🎯 cibles : sous ref + overlap horizontal
    const targets = nodes
      .filter(
        (n) =>
          n !== refNode &&
          n.y >= refBottom &&
          n.x <= refX2 &&
          n.x + n.w - 1 >= refX1
      )
      .sort((a, b) => a.y - b.y || a.x - b.x);

    // console.log("ref:", {
    //   id: refNode.el?.dataset?.id,
    //   x: refNode.x,
    //   y: refNode.y,
    //   w: refNode.w,
    //   h: refNode.h,
    // });
    // console.log(
    //   "targets:",
    //   targets.map((n) => n.el?.dataset?.id)
    // );

    // --- compact ---
    this._isInternalGridUpdate = true;
    try {
      grid.batchUpdate();

      for (const node of targets) {
        let newY = refBottom;
        let guard = 0;

        while (
          grid.engine.collide(node, {
            x: node.x,
            y: newY,
            w: node.w,
            h: node.h,
          }) &&
          guard++ < 2000
        )
          newY++;

        if (guard >= 2000) {
          console.warn("⚠️ guard hit for node", node.el?.dataset?.id);
        }

        if (newY !== node.y) {
          grid.engine.moveNode(node, {
            x: node.x,
            y: newY,
            w: node.w,
            h: node.h,
          });
        }

        refBottom = Math.max(refBottom, newY + node.h);
      }

      grid.commit();
    } finally {
      this._isInternalGridUpdate = false;
    }

    if (typeof grid._updateStyles === "function") grid._updateStyles(true);

    // --- snapshot AFTER + diff => changed (cascade incluse) ---
    const changed = [];
    grid.engine.nodes.forEach((n) => {
      const id = n?.el?.dataset?.id;
      if (!id) return;
      const b = before.get(id);
      if (!b) return;

      if (b.x !== n.x || b.y !== n.y || b.w !== n.w || b.h !== n.h) {
        changed.push(n);
      }
    });

    // console.log(
    //   "✅ changed nodes:",
    //   changed.map((n) => n.el?.dataset?.id)
    // );

    console.groupEnd();
    return { targets, changed };
  }

  compactAllWidgetsUp() {
    if (!this.grid?.engine?.nodes) {
      console.warn("⛔️ Grid not available.");
      return;
    }

    // 🧩 Tri stable : par colonne, puis par Y croissant
    const sorted = this.grid.engine.nodes
      .slice()
      .sort((a, b) => a.x - b.x || a.y - b.y);

    sorted.forEach((node) => {
      let newY = 0;

      // On cherche la première position libre vers le haut
      while (
        this.grid.engine.collide(node, {
          x: node.x,
          y: newY,
          w: node.w,
          h: node.h,
        })
      ) {
        newY++;
      }

      if (newY !== node.y) {
        // console.log(
        //   `⬆️ Widget ${node.el?.dataset?.id} moved from y=${node.y} → y=${newY}`
        // );
        this.grid.engine.moveNode(node, {
          x: node.x,
          y: newY,
          w: node.w,
          h: node.h,
        });
      }
    });

    // console.log("🔄 Triggering change + updating styles (global compact up)");
    this.grid._triggerChangeEvent();
    this.grid._updateStyles(true);
  }
  reconnectStimulusControllers(container) {
    container.querySelectorAll("[data-controller]").forEach((el) => {
      const controllers = el.getAttribute("data-controller").split(" ");
      controllers.forEach((controller) => {
        application
          .getControllerForElementAndIdentifier(el, controller)
          ?.connect();
      });
    });
  }

  getPopupMessage() {
    fetch(this.popupMessageUrlValue, {
      headers: {
        Accept: "text/html",
        "X-Requested-With": "XMLHttpRequest",
      },
    })
      .then((response) => response.text())
      .then((html) => {
        const popup = document.getElementById("popup-content");

        if (popup) {
          popup.innerHTML = html;

          // Vérifie si #fx_modal existe dans le contenu injecté
          const modal = popup.querySelector("#fx_modal");

          if (modal) {
            Fancybox.show([{ src: "#popup-content", type: "inline" }]);
          }
        }
      })
      .catch((error) => {
        console.error("Erreur lors du chargement du popup :", error);
      });
  }

  toggleAutoresize(event) {
    const itemEl = event.currentTarget.closest(".grid-stack-item");
    const dashSettingId = itemEl.dataset.id;
    const icon = event.currentTarget.querySelector("i");

    const currentValue = parseInt(icon.dataset.autoResize, 10);
    const isEnabled = currentValue === 1;

    // Toggle dataset
    icon.dataset.autoResize = isEnabled ? 2 : 1;

    // Toggle classes
    icon.classList.toggle("fa-toggle-on", !isEnabled);
    icon.classList.toggle("fa-toggle-off", isEnabled);

    // Toggle title
    icon.title = isEnabled
      ? "Redimensionnement auto désactivé"
      : "Redimensionnement auto activé";

    const newValue = isEnabled ? 2 : 1;

    fetch(
      `/${this.currentAppValue}/fdashboards/${dashSettingId}/toggle_auto_resize`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector("meta[name='csrf-token']")
            .content,
        },
        body: JSON.stringify({ auto_resize: newValue }),
      }
    );
    if (newValue === 1) {
      // auto-resize activé → comportement normal
      // this.resizeToContentFull(event);
      this.onWidgetContentUpdated(dashSettingId);
    } else {
      // auto-resize désactivé → hauteur forcée 250px
      this.setFixedHeight(itemEl);
    }
  }

  // utils déjà utilisés dans ton fichier
  getFullHeight(el) {
    if (!el) return 0;
    const cs = window.getComputedStyle(el);
    return (
      el.offsetHeight +
      parseFloat(cs.marginTop || 0) +
      parseFloat(cs.marginBottom || 0)
    );
  }

  getContentSize(el) {
    if (!el) return { width: 0, height: 0 };
    return { width: el.scrollWidth, height: el.scrollHeight };
  }
}
